import {
    Autocomplete,
    IconButton,
    Stack,
    SxProps,
    TextField,
    Tooltip,
} from '@mui/material'
import { forwardRef, useRef, useState } from 'react'
import {
    ClientInfo,
    anonymousClient,
    useClient,
} from '../../context/client_context'
import { getClientLabel } from '../../context/sale_context'
import { listClients } from '../../services/client'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../context/account_context'
import { Edit } from '@mui/icons-material'
import { useCoin } from '../../context/coin_context'

interface Props {
    client: ClientInfo | null
    returnClient(client: ClientInfo): void
    showAnonymousClient: boolean
    hideNewClient?: boolean
    sx?: SxProps
    inputSx?: SxProps
    size?: 'small' | 'medium'
    endAdornment?: JSX.Element
    disabled?: boolean
    customLabel?: string
    showEditClient?: boolean
}

export const ClientSearchBar = forwardRef<HTMLInputElement, Props>(
    (
        {
            client,
            returnClient,
            showAnonymousClient,
            sx,
            inputSx,
            size,
            endAdornment,
            disabled,
            customLabel,
            showEditClient,
        },
        ref
    ) => {
        const { t } = useTranslation()
        const [clients, setClients] = useState<ClientInfo[]>(
            showAnonymousClient ? [anonymousClient] : []
        )
        const timeout = useRef<NodeJS.Timeout>()
        const { accountInfo } = useAccount()
        const { coins } = useCoin()
        const { setOpenRegisterClientModal, editClient } = useClient()

        const searchClients = async (value: string) => {
            if (!value) return
            try {
                const res = await listClients(
                    {
                        skip: 1,
                        take: 10,
                        search: value,
                        dontCount: true,
                    },
                    coins
                )
                if (showAnonymousClient)
                    res.data.unshift({ ...anonymousClient })
                setClients(res.data)
            } catch (error) {}
        }

        const canUpdateClient =
            accountInfo?.isMaster ||
            !!accountInfo?.permissions?.includes('Clientes:Atualizar')

        const showEditIcon = canUpdateClient && !!client?.id && showEditClient

        return (
            <>
                <Autocomplete
                    options={clients}
                    filterOptions={(p) => p}
                    getOptionLabel={getClientLabel}
                    sx={{ width: '100%', ...sx }}
                    value={client}
                    disabled={disabled}
                    onChange={(_, newClient) => {
                        if (!newClient) return
                        returnClient(newClient)
                    }}
                    blurOnSelect={true}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            onChange={(e) => {
                                const { value } = e.target
                                clearTimeout(timeout.current)
                                timeout.current = setTimeout(() => {
                                    searchClients(value)
                                }, 250)
                            }}
                            label={
                                customLabel
                                    ? t(customLabel)
                                    : t('Buscar cliente')
                            }
                            sx={{ width: '100%' }}
                            inputRef={ref}
                            size={size ? size : 'small'}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <Stack
                                        direction="row"
                                        alignItems={'center'}
                                        position={'absolute'}
                                        right={'10px'}
                                    >
                                        {showEditIcon && (
                                            <Tooltip
                                                title={t('Editar cliente')}
                                            >
                                                <IconButton
                                                    onClick={() => {
                                                        setOpenRegisterClientModal(
                                                            true
                                                        )
                                                        editClient(
                                                            client.id as number
                                                        )
                                                    }}
                                                >
                                                    <Edit color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {endAdornment}
                                    </Stack>
                                ),
                                sx: inputSx,
                            }}
                        />
                    )}
                />
            </>
        )
    }
)
